<template>
  <div ref="root" class="pcs">
    <button
      ref="triggerEl"
      type="button"
      class="pcs-trigger"
      :class="{ 'is-open': open }"
      aria-haspopup="listbox"
      :aria-expanded="open"
      :aria-label="`Country calling code: ${selected.name} ${selected.dial}`"
      @click="toggle"
      @keydown.down.prevent="openList()"
    >
      <span class="pcs-flag" aria-hidden="true">{{ selected.flag }}</span>
      <span class="pcs-dial">{{ selected.dial }}</span>
      <svg class="pcs-chevron" viewBox="0 0 12 8" aria-hidden="true">
        <path d="M1.4 1.6 6 6.2l4.6-4.6" fill="none" stroke="currentColor" stroke-width="1.6"
          stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <div v-if="open" class="pcs-pop" :class="{ 'is-up': openUp }">
      <div class="pcs-search">
        <svg class="pcs-search-icon" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="7" cy="7" r="4.4" fill="none" stroke="currentColor" stroke-width="1.5" />
          <path d="m10.4 10.4 3 3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
        <input
          ref="searchEl"
          v-model="query"
          type="text"
          class="pcs-search-input"
          placeholder="Search country or code"
          aria-label="Search country or code"
          autocomplete="off"
          spellcheck="false"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="chooseActive"
          @keydown.esc.prevent.stop="close(true)"
          @keydown.tab="close(false)"
        />
      </div>

      <ul
        ref="listEl"
        class="pcs-list"
        role="listbox"
        aria-label="Country calling code"
        :style="{ maxHeight: listMaxHeight }"
      >
        <template v-for="(country, index) in filtered" :key="country.iso">
          <li>
            <button
              type="button"
              role="option"
              class="pcs-item"
              :class="{ 'is-active': index === activeIndex, 'is-selected': country.iso === modelValue }"
              :aria-selected="country.iso === modelValue"
              :data-index="index"
              @click="choose(country)"
              @mousemove="activeIndex = index"
            >
              <span class="pcs-item-flag" aria-hidden="true">{{ country.flag }}</span>
              <span class="pcs-item-name">{{ country.name }}</span>
              <span class="pcs-item-dial">{{ country.dial }}</span>
            </button>
          </li>
          <li v-if="!query && index === pinnedCount - 1" class="pcs-divider" aria-hidden="true"></li>
        </template>
        <li v-if="!filtered.length" class="pcs-empty">No country matches “{{ query }}”.</li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { countryCodeOptions, phoneCountryCodes } from '~/data/phone-country-codes';

type Country = { name: string; iso: string; dial: string; flag: string };

const props = defineProps<{ modelValue: string }>();
const emit = defineEmits<{ 'update:modelValue': [string] }>();

const pinnedCount = phoneCountryCodes.length;
const options = countryCodeOptions as Country[];

const root = ref<HTMLElement | null>(null);
const triggerEl = ref<HTMLButtonElement | null>(null);
const searchEl = ref<HTMLInputElement | null>(null);
const listEl = ref<HTMLElement | null>(null);

const open = ref(false);
const openUp = ref(false);
const query = ref('');
const activeIndex = ref(0);
const listMaxHeight = ref('15rem');

const GUTTER = 10; // breathing room against the clipping edge
const CHROME = 52; // search row + list padding
const MIN_POP = 210; // below this the popover is worth flipping
const MIN_LIST = 132;
const MAX_LIST = 240;

const selected = computed<Country>(
  () => options.find((country) => country.iso === props.modelValue) || options[0],
);

const filtered = computed(() => {
  const term = query.value.trim().toLowerCase();
  if (!term) return options;
  // Match on country name or dial code, with or without the leading "+".
  const digits = term.replace(/^\+/, '');
  return options.filter(
    (country) =>
      country.name.toLowerCase().includes(term) ||
      country.iso.toLowerCase() === term ||
      (digits && country.dial.replace('+', '').startsWith(digits)),
  );
});

watch(query, () => {
  activeIndex.value = 0;
  scrollActiveIntoView();
});

const scrollActiveIntoView = async () => {
  await nextTick();
  listEl.value
    ?.querySelector<HTMLElement>(`[data-index="${activeIndex.value}"]`)
    ?.scrollIntoView({ block: 'nearest' });
};

const move = (delta: number) => {
  if (!filtered.value.length) return;
  const next = activeIndex.value + delta;
  activeIndex.value = next < 0 ? filtered.value.length - 1 : next % filtered.value.length;
  scrollActiveIntoView();
};

const choose = (country: Country) => {
  emit('update:modelValue', country.iso);
  close(true);
};

const chooseActive = () => {
  const country = filtered.value[activeIndex.value];
  if (country) choose(country);
};

function close(refocus: boolean) {
  if (!open.value) return;
  open.value = false;
  query.value = '';
  if (refocus) triggerEl.value?.focus();
}

// Nearest ancestor that clips its overflow — the dialog body scrolls, so the
// popover has to be measured against that box, not the viewport.
const clippingParent = (element: HTMLElement | null) => {
  let node = element?.parentElement || null;
  while (node) {
    if (/(auto|scroll|hidden)/.test(getComputedStyle(node).overflowY)) return node;
    node = node.parentElement;
  }
  return null;
};

const openList = async () => {
  if (open.value) return;
  const rect = triggerEl.value?.getBoundingClientRect();
  if (rect) {
    const clip = clippingParent(triggerEl.value)?.getBoundingClientRect();
    const top = Math.max(0, clip?.top ?? 0);
    const bottom = Math.min(window.innerHeight, clip?.bottom ?? window.innerHeight);
    const below = bottom - rect.bottom - GUTTER;
    const above = rect.top - top - GUTTER;
    // Flip up only when that genuinely buys room, then size the list to fit.
    openUp.value = below < MIN_POP && above > below;
    listMaxHeight.value = `${Math.round(
      Math.min(MAX_LIST, Math.max(MIN_LIST, (openUp.value ? above : below) - CHROME)),
    )}px`;
  }
  open.value = true;
  activeIndex.value = Math.max(
    0,
    options.findIndex((country) => country.iso === props.modelValue),
  );
  await nextTick();
  searchEl.value?.focus();
  scrollActiveIntoView();
};

const toggle = () => (open.value ? close(true) : openList());

const onDocumentPointerDown = (event: PointerEvent) => {
  if (!open.value) return;
  if (!root.value?.contains(event.target as Node)) close(false);
};

watch(open, (isOpen) => {
  if (isOpen) document.addEventListener('pointerdown', onDocumentPointerDown, true);
  else document.removeEventListener('pointerdown', onDocumentPointerDown, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
});
</script>

<style scoped>
.pcs {
  position: relative;
}

.pcs-trigger {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  background-color: #f8fafc;
  color: #0f172a;
  padding: 0.7rem 0.7rem;
  font-size: 0.9rem;
  line-height: 1.2;
  cursor: pointer;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease;
}

.pcs-trigger:hover {
  border-color: #94a3b8;
}

.pcs-trigger.is-open {
  border-color: rgb(27 167 206 / 0.7);
  background-color: #fff;
  box-shadow: 0 0 0 3px rgb(27 167 206 / 0.14);
}

.pcs-trigger:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 3px;
}

.pcs-flag {
  font-size: 1rem;
  line-height: 1;
}

.pcs-dial {
  font-variant-numeric: tabular-nums;
  font-weight: 650;
}

.pcs-chevron {
  width: 0.6rem;
  height: 0.6rem;
  margin-left: auto;
  color: #94a3b8;
  transition: transform 0.18s ease;
}

.pcs-trigger.is-open .pcs-chevron {
  color: #1ba7ce;
  transform: rotate(180deg);
}

.pcs-pop {
  position: absolute;
  z-index: 40;
  top: calc(100% + 0.4rem);
  left: 0;
  width: max(100%, 18rem);
  max-width: min(20rem, calc(100vw - 2.5rem));
  overflow: hidden;
  border: 1px solid rgb(203 213 225 / 80%);
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 18px 44px rgb(2 6 23 / 18%);
  animation: pcs-in 0.14s ease-out;
}

.pcs-pop.is-up {
  top: auto;
  bottom: calc(100% + 0.4rem);
  transform-origin: bottom left;
}

@keyframes pcs-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pcs-pop {
    animation: none;
  }
  .pcs-chevron {
    transition: none;
  }
}

.pcs-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 1px solid rgb(203 213 225 / 65%);
  padding: 0.6rem 0.75rem;
}

.pcs-search-icon {
  width: 0.85rem;
  height: 0.85rem;
  flex: none;
  color: #94a3b8;
}

.pcs-search-input {
  width: 100%;
  border: 0;
  background: transparent;
  color: #0f172a;
  padding: 0;
  font-size: 0.85rem;
}

.pcs-search-input::placeholder {
  color: #94a3b8;
}

/* The popover itself is the focus context, so the search field does not need a
   ring of its own — it is the only text input inside it. */
.pcs-search-input:focus,
.pcs-search-input:focus-visible {
  outline: none;
  box-shadow: none;
}

.pcs-list {
  overflow-y: auto;
  margin: 0;
  padding: 0.35rem;
  list-style: none;
  overscroll-behavior: contain;
}

.pcs-list::-webkit-scrollbar {
  width: 10px;
}

.pcs-list::-webkit-scrollbar-thumb {
  border: 3px solid transparent;
  border-radius: 999px;
  background-clip: content-box;
  background-color: rgb(148 163 184 / 55%);
}

.pcs-item {
  display: grid;
  width: 100%;
  grid-template-columns: 1.15rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.6rem;
  border: 0;
  border-radius: 8px;
  background: transparent;
  padding: 0.5rem 0.55rem;
  text-align: left;
  cursor: pointer;
}

.pcs-item.is-active {
  background: #f1f5f9;
}

.pcs-item.is-selected {
  background: rgb(27 167 206 / 0.09);
}

.pcs-item:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: -2px;
}

.pcs-item-flag {
  font-size: 0.95rem;
  line-height: 1;
}

.pcs-item-name {
  overflow: hidden;
  color: #0f172a;
  font-size: 0.85rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pcs-item-dial {
  color: #64748b;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
}

.pcs-item.is-selected .pcs-item-name,
.pcs-item.is-selected .pcs-item-dial {
  color: #0e8eb3;
}

.pcs-divider {
  height: 1px;
  margin: 0.35rem 0.55rem;
  background: rgb(203 213 225 / 70%);
}

.pcs-empty {
  color: #64748b;
  padding: 0.9rem 0.6rem;
  font-size: 0.82rem;
}

/* Dark theme */
.dark .pcs-trigger {
  border-color: #334155;
  background-color: #020617;
  color: #fff;
}

.dark .pcs-trigger:hover {
  border-color: #475569;
}

.dark .pcs-trigger.is-open {
  border-color: rgb(27 167 206 / 0.75);
  background-color: #020617;
}

.dark .pcs-pop {
  border-color: #334155;
  background: #0b1220;
  box-shadow: 0 18px 44px rgb(2 6 23 / 55%);
}

.dark .pcs-search {
  border-bottom-color: #1e293b;
}

.dark .pcs-search-input {
  color: #fff;
}

.dark .pcs-item-name {
  color: #e2e8f0;
}

.dark .pcs-item-dial {
  color: #94a3b8;
}

.dark .pcs-item.is-active {
  background: #1e293b;
}

.dark .pcs-item.is-selected {
  background: rgb(27 167 206 / 0.16);
}

.dark .pcs-item.is-selected .pcs-item-name,
.dark .pcs-item.is-selected .pcs-item-dial {
  color: #67d3f0;
}

.dark .pcs-divider {
  background: #1e293b;
}

.dark .pcs-empty {
  color: #94a3b8;
}
</style>
